import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api } from '@/lib/api'
import { clientDashboardKeys } from './clientDashboards'
import type { ClientKpis, KpiSettings, KpiSettingsResponse } from '@/types/clientKpis'

export const kpiSettingsKey = ['kpi-settings'] as const
export const clientKpisKey = (companyId: string) => [...clientDashboardKeys.all, 'kpis', companyId] as const

export function useClientKpis(companyId: string) {
  const queryClient = useQueryClient()
  return useQuery({
    queryKey: clientKpisKey(companyId),
    queryFn: async () => {
      const response = await api<{ data: ClientKpis }>(`/client-dashboards/${companyId}/kpis`)
      // Opening the tab saves fresh figures, which the header and the Clients list read.
      queryClient.invalidateQueries({ queryKey: clientDashboardKeys.detail(companyId) })
      queryClient.invalidateQueries({ queryKey: [...clientDashboardKeys.all, 'list'] })
      return response
    },
    // Each load rebuilds and saves the live figures, so refocusing the window shouldn't redo it.
    staleTime: 5 * 60_000,
  })
}

export function useKpiSettings() {
  return useQuery({
    queryKey: kpiSettingsKey,
    queryFn: () => api<{ data: KpiSettingsResponse }>('/client-dashboards/kpi-settings'),
    // A focus refetch would remount the editor over an unsaved draft; a save refreshes the settings itself.
    refetchOnWindowFocus: false,
  })
}

function useSettingsMutation<T>(mutationFn: (input: T) => Promise<{ data: KpiSettingsResponse }>) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn,
    onSuccess: response => {
      queryClient.setQueryData(kpiSettingsKey, response)
      queryClient.invalidateQueries({ queryKey: clientDashboardKeys.all })
    },
  })
}

export function useSaveKpiSettings() {
  return useSettingsMutation((settings: KpiSettings) =>
    api<{ data: KpiSettingsResponse }>('/client-dashboards/kpi-settings', { method: 'PUT', body: { settings } }),
  )
}

export function useResetKpiSettings() {
  return useSettingsMutation<void>(() => api<{ data: KpiSettingsResponse }>('/client-dashboards/kpi-settings/reset', { method: 'POST' }))
}
